import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { SummaryView } from "@/components/SummaryView";
import type { Summary } from "@/types";
import { getDateLocale } from "@/lib/dateFormat";

interface PublicData {
  meeting: {
    id: string;
    title: string | null;
    recorded_at: string;
    duration_seconds: number | null;
    template_key: string;
    language: string;
  };
  summary: Summary | null;
  transcript: string | null;
  include_transcript: boolean;
}

export function PublicMeeting() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [data, setData] = useState<PublicData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("Invalid link");
      setLoading(false);
      return;
    }
    supabase.functions
      .invoke("public-meeting", { body: { token } })
      .then(({ data, error }) => {
        if (error) {
          setError(error.message);
        } else if ((data as { error?: string })?.error) {
          setError((data as { error: string }).error);
        } else {
          setData(data as PublicData);
        }
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-redi-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-redi-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-redi-dark flex flex-col items-center justify-center px-6">
        <p className="text-redi-text text-lg font-semibold mb-2">
          {t("publicShare.unavailableTitle")}
        </p>
        <p className="text-redi-muted text-sm text-center mb-6">
          {error ?? t("publicShare.unavailableMsg")}
        </p>
        <a
          href="https://redi.axisapps.app"
          className="text-redi-primary text-sm hover:underline"
        >
          {t("publicShare.discoverRedi")}
        </a>
      </div>
    );
  }

  const date = new Date(data.meeting.recorded_at).toLocaleDateString(
    getDateLocale(),
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );

  return (
    <div className="min-h-screen bg-redi-dark">
      <header className="border-b border-white/5 px-6 py-4 flex items-center justify-between">
        <a href="https://redi.axisapps.app" className="text-redi-text font-bold">
          Rédi
        </a>
        <span className="text-redi-muted text-xs">
          {t("publicShare.readonly")}
        </span>
      </header>

      <main className="max-w-3xl mx-auto p-6">
        <h1 className="text-redi-text text-2xl font-bold mb-1">
          {data.meeting.title ?? t("publicShare.untitled")}
        </h1>
        <p className="text-redi-muted text-sm mb-6">
          {date}
          {data.meeting.duration_seconds != null
            ? ` · ${Math.floor(data.meeting.duration_seconds / 60)} min`
            : ""}
        </p>

        {data.summary ? (
          <SummaryView summary={data.summary} />
        ) : (
          <p className="text-redi-muted text-sm">{t("publicShare.noSummary")}</p>
        )}

        {data.include_transcript && data.transcript && (
          <section className="mt-8">
            <h2 className="text-redi-text text-lg font-semibold mb-3">
              {t("transcript.title")}
            </h2>
            <div className="bg-redi-surface rounded-xl p-5">
              <p className="text-redi-text text-sm whitespace-pre-wrap leading-6">
                {data.transcript}
              </p>
            </div>
          </section>
        )}

        <footer className="mt-12 pt-6 border-t border-white/5 text-center">
          <p className="text-redi-muted text-xs mb-2">
            {t("publicShare.poweredBy")}
          </p>
          <a
            href="https://redi.axisapps.app"
            className="text-redi-primary text-sm font-semibold hover:underline"
          >
            redi.axisapps.app
          </a>
        </footer>
      </main>
    </div>
  );
}
